(function () {
  /**
   * @controller AssetLifeCycleDialogController
   *
   * @see UpdateAssetLifeCycleDialogService#show
   */
  angular
    .module("akitabox.ui.dialogs.asset.updateLifeCycle")
    .controller(
      "UpdateAssetLifeCycleDialogController",
      UpdateAssetLifeCycleDialogController
    );

  /* @ngInject */
  function UpdateAssetLifeCycleDialogController(
    $mdDialog,
    $q,
    $scope,
    enums,
    AssetService,
    CancellableService,
    CostLineService,
    OrganizationService,
    ConfirmationDialog
  ) {
    // ===============
    // Properties
    // ===============
    this.QUANTITY_UNITS = Object.values(enums.QUANTITY_UNIT.DISPLAY_SHORT);

    this.organization = OrganizationService.getCurrent();

    /**
     * @property { Asset[] }
     * Provided in `locals` by the service.
     */
    this.assets;

    /**
     * @property {string|undefined} errorMessage holds the error messages generated from API calls
     */
    this.errorMessage = "";

    /**
     * @property {number} progress progress of the bulk action of assigning each asset to a cost line
     */
    this.progress = 0;

    /**
     * @property {string} lastAction helps us know what to retry, the save or delete
     */
    this.lastAction = "";

    this.catalogOptions = [
      {
        key: "custom",
        value: "Custom",
        isCustom: true,
        model: {
          name: "Custom",
        },
      },
    ];
    this.rsMeansCatalogOptions = [
      {
        key: "fmr",
        value: "Facilities Maintenance and Repair",
        group: "RS Means",
        isRSMeans: true,
        model: {
          name: "Facilities Maintenance and Repair",
        },
      },
      {
        key: "cnc",
        value: "Commercial New Construction",
        group: "RS Means",
        isRSMeans: true,
        model: {
          name: "Commercial New Construction",
        },
      },
      {
        key: "fcr",
        value: "Facilities and Commercial Renovation",
        group: "RS Means",
        isRSMeans: true,
        model: {
          name: "Facilities and Commercial Renovation",
        },
      },
    ];

    this.installationYear = null;
    this.assessedYear = null;
    this.scheduledYear = null;
    this.yearsDeferred = null;

    this.suggestedDueDate = "Not Provided";
    this.suggestedAssessedYear = null;
    this.suggestedScheduledYear = null;

    this.suggested_interval = null;
    this.suggested_due_date = null;
    this.suggested_cost = 0;
    this.quantity = null;
    this.unitCost = null;
    this.totalCost = 0;
    this.suggestedTotalCost = 0;

    // Override fields for user edits (null means use catalog/existing values)
    this.quantityOverride = null;
    this.unitCostOverride = null;

    this.isSaving = false;
    this.isSavingInstallationDate = false;
    this.isDeleting = false;
    this.isEditingInstallationDate = false;

    this.isSectionInvalid = false;

    this.searchText = null;
    this.catalogItems = [];
    this.selectedCatalogItem = null;

    const _formatUnit = (enumKey, value, variant) => {
      if (!enumKey || !value) return "";

      let formattedValue = null;
      const enumObj = enums[enumKey];

      switch (variant) {
        case "long":
          formattedValue = enumObj?.DISPLAY_LONG[value];
          break;
        case "short":
        default:
          formattedValue = enumObj?.DISPLAY_SHORT[value];
          break;
      }

      return formattedValue ? formattedValue : value;
    };

    /**
     * pipeline to hold a series of cancellable async functions
     * mostly for our bulk work of assigning each asset within this dialog
     * to the selected cost line
     */
    const asyncPipeline = CancellableService.createPipeline("canceled");

    this.setTotalCost = () => {
      if (this.quantity) {
        if (this.suggested_cost) {
          this.suggestedTotalCost = this.quantity * this.suggested_cost;

          if (!this.unitCost) {
            this.totalCost = this.quantity * this.suggested_cost;
          }
        }
        if (this.unitCost) {
          this.totalCost = this.quantity * this.unitCost;
        }
      }
      if (!this.suggested_cost) {
        this.suggestedTotalCost = 0;
      }
      if (!this.unitCost && !this.suggested_cost) {
        this.totalCost = 0;
      }
    };

    this.updateTotalCostFromOverrides = () => {
      // Calculate total cost based on override values if both are provided
      const effectiveQuantity =
        this.quantityOverride !== null && this.quantityOverride !== undefined
          ? this.quantityOverride
          : this.quantity;
      const effectiveUnitCost =
        this.unitCostOverride !== null && this.unitCostOverride !== undefined
          ? this.unitCostOverride
          : this.unitCost;

      if (effectiveQuantity && effectiveUnitCost) {
        this.totalCost = effectiveQuantity * effectiveUnitCost;
      } else if (this.quantity && this.unitCost) {
        this.totalCost = this.quantity * this.unitCost;
      } else {
        this.totalCost = 0;
      }
    };

    this.setYearsDeferred = () => {
      if (this.scheduledYear && this.assessedYear) {
        this.yearsDeferred = this.scheduledYear - this.assessedYear;
      } else {
        this.yearsDeferred = null;
      }
    };

    this.updateField = angular.debounce((event, fieldName) => {
      let value = event.value;
      switch (fieldName) {
        case "installation_date":
          this.installationYear = value;
          break;
        case "assessed_due_date":
          this.assessedYear = value;
          break;
        case "scheduled_due_date":
          this.scheduledYear = value;
          break;
        case "quantity":
          this.quantityOverride = value;
          this.updateTotalCostFromOverrides();
          break;
        case "unit_cost":
          this.unitCostOverride = value;
          this.updateTotalCostFromOverrides();
          break;
        // These fields are read-only, ignore updates
        case "description":
          break;
      }

      this.setYearsDeferred();
    }, 500);

    this.updateInstallationDate = (asset) => {
      const newDate = this.installationYear
        ? new Date(this.installationYear, 0, 2)
        : null;
      const assetDate = asset.installation_date
        ? new Date(asset.installation_date)
        : null;
      if (assetDate === null && newDate === null) {
        return $q.resolve();
      } else if (
        assetDate &&
        newDate &&
        assetDate.getUTCFullYear() === newDate.getUTCFullYear()
      ) {
        return $q.resolve();
      }
      return AssetService.updateById(asset.building, asset._id, {
        installation_date: newDate.getTime(),
      });
    };

    this.updateCostLine = (asset, costLine) => {
      const assessedDueDate = this.assessedYear
        ? new Date(this.assessedYear, 0, 1)
        : null;
      const scheduledDueDate = this.scheduledYear
        ? new Date(this.scheduledYear, 0, 1)
        : null;
      const currentAssessedDueDate = costLine.assessed_due_date
        ? new Date(costLine.assessed_due_date)
        : null;
      const currentScheduledDueDate = costLine.scheduled_due_date
        ? new Date(costLine.scheduled_due_date)
        : null;

      const data = {};

      if (
        assessedDueDate &&
        (!currentAssessedDueDate ||
          currentAssessedDueDate.getUTCFullYear() !==
            assessedDueDate.getUTCFullYear())
      ) {
        data.assessed_due_date = assessedDueDate.getTime();
      }
      if (
        scheduledDueDate &&
        (!currentScheduledDueDate ||
          currentScheduledDueDate.getUTCFullYear() !==
            scheduledDueDate.getUTCFullYear())
      ) {
        data.scheduled_due_date = scheduledDueDate.getTime();
      }

      // Only include quantity if override is set and different from current
      if (
        this.quantityOverride !== null &&
        this.quantityOverride !== undefined
      ) {
        if (this.quantityOverride !== costLine.quantity) {
          data.quantity = this.quantityOverride;
        }
      }

      // Only include unit cost if override is set and different from current
      if (
        this.unitCostOverride !== null &&
        this.unitCostOverride !== undefined
      ) {
        if (this.unitCostOverride !== costLine.unit_cost) {
          data.user_cost = this.unitCostOverride;
        }
      }

      if (Object.keys(data).length === 0) return $q.resolve();

      return CostLineService.updateById(asset.building, costLine._id, data);
    };

    this.declassifyAsset = (asset) => {
      return AssetService.classify(asset.building, asset._id, null);
    };

    this.reclassifyAsset = (asset) => {
      return this.declassifyAsset(asset).then(() => {
        return this.classifyAsset(asset);
      });
    };

    this.setSectionInvalid = (isInvalid) => {
      this.isSectionInvalid = isInvalid;
    };

    this.toDollars = (cents) => {
      if (!cents) return 0;
      const centsNumber = Number(cents);
      if (Number.isNaN(centsNumber)) return 0;
      return centsNumber / 100;
    };

    this.toCurrency = (cents) => {
      const dollars = this.toDollars(cents);
      if (dollars === 0) return "$0.00";
      const { format } = new Intl.NumberFormat("en-US", {
        currency: "USD",
        style: "currency",
      });
      return format(dollars);
    };

    this.formatSizeUnit = (value, variant) => {
      return _formatUnit("SIZE_UNIT", value, variant);
    };

    this.formatQuantityUnit = (value, variant) => {
      return _formatUnit("QUANTITY_UNIT", value, variant);
    };

    /**
     * @method cancel
     * Cancel the dialog
     */
    this.cancel = () => {
      if (this.isSaving || this.isDeleting) {
        asyncPipeline.cancel();
      } else {
        $mdDialog.hide(this.costLineDeleted || this.costLineCostUpdated);
      }
    };

    this.reset = () => {
      this.assessedYear = null;
      this.scheduledYear = null;
      this.unitCost = 0;
      this.suggested_cost = 0;
      this.quantity = null;
      this.quantityOverride = null;
      this.unitCostOverride = null;
      this.setSuggestedDueDates();
      this.setYearsDeferred();
      this.setTotalCost();

      this.$onInit();
    };

    this.saveInstallationDate = () => {
      if (!this.assets.length) {
        return $q.resolve();
      }

      this.progress = 0;
      this.errorMessage = "";
      this.isSavingInstallationDate = true;
      this.lastAction = "saveInstallationDate";

      const fns = [];
      let count = 0;

      this.assets.forEach((asset) => {
        fns.push(() => {
          return this.updateInstallationDate(asset).then(() => {
            count++;
            this.progress = Math.floor((count / this.assets.length) * 100);
          });
        });
      });

      return asyncPipeline
        .switchTo(CancellableService.executeSeries(fns))
        .then(() => {
          this.isSavingInstallationDate = false;
          this.isEditingInstallationDate = false;
        })
        .catch((err) => {
          const remaining = this.assets.length - count;
          if (err !== "canceled") {
            this.errorMessage = `Error occured. ${remaining} assets could not be updated`;
            asyncPipeline.cancel(); // cancel remaining ones
          }
          // $mdDialog.hide() removes this from the DOM, so we can't put setSaving in a finally
          this.isSavingInstallationDate = false;
          this.isEditingInstallationDate = true;
        });
    };

    this.save = () => {
      if (!this.assets.length) {
        return $q.resolve();
      }

      this.progress = 0;
      this.errorMessage = "";
      this.isSaving = true;
      this.lastAction = "save";

      const fns = [];
      let count = 0;

      // Only update dates, not cost/catalog/quantity fields
      this.assets.forEach((asset) => {
        fns.push(() => {
          return CostLineService.getOneByAsset(asset)
            .then((costLine) => {
              if (costLine) {
                return this.updateCostLine(asset, costLine);
              }
            })
            .then(() => {
              count++;
              this.progress = Math.floor((count / this.assets.length) * 100);
            });
        });
      });

      return asyncPipeline
        .switchTo(CancellableService.executeSeries(fns))
        .then(() => {
          this.isSaving = false;

          $mdDialog.hide(true);
        })
        .catch((err) => {
          const remaining = this.assets.length - count;
          if (err !== "canceled") {
            this.errorMessage = `Error occured. ${remaining} assets could not be updated`;
            asyncPipeline.cancel(); // cancel remaining ones
          }
          // $mdDialog.hide() removes this from the DOM, so we can't put setSaving in a finally
          this.isSaving = false;
        });
    };

    this.delete = () => {
      ConfirmationDialog.show({
        title:
          this.assets.length > 1
            ? `Remove costs from all ${this.assets.length} assets?`
            : `Remove costs from ${this.assets[0].name}`,
        prompt:
          this.assets.length > 1
            ? `Are you sure you want remove the cost from each of these ${this.assets.length} assets?`
            : `Are you sure you want to remove ${this.assets[0].name}'s cost?`,
        isDestructive: true,
        confirmText: "Remove",
        onConfirm: () => {
          this.progress = 0;
          this.errorMessage = "";
          this.isDeleting = true;
          this.lastAction = "delete";
          this.costLineDeleted = true;

          let count = 0;
          const fns = [];

          this.assets.forEach((asset) => {
            fns.push(() => {
              return this.declassifyAsset(asset).then(() => {
                count++;
                this.progress = Math.floor((count / this.assets.length) * 100);
              });
            });
          });

          // We don't return the promise here becuase we don't want the dialog
          // to wait on all the deletes. We want to go back to the selection dialog
          // and be able to cancel during the delete if needed
          asyncPipeline
            .switchTo(CancellableService.executeSeries(fns))
            .then(() => {
              this.reset();
            })
            .catch((err) => {
              const remaining = this.assets.length - count;
              if (err !== "canceled") {
                // probably some api error, we need to alert and cancel any remaining requests
                this.errorMessage = `Error occured. Remaining ${remaining} assets could not be updated`;
                asyncPipeline.cancel();
              }
            })
            .finally(() => {
              this.isDeleting = false;
            });
        },
      });
    };

    this.retry = () => {
      if (this.lastAction === "delete") {
        this.delete();
      } else if (this.lastAction === "save") {
        this.save();
      } else {
        // just make the button and error go away
        this.errorMessage = "";
        this.isSaving = false;
        this.isDeleting = false;
      }
    };
  }
})();
